import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UploadCloud, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Inventory bulk import: accepts CSV / XLSX / XLS. Chef uploads a spreadsheet
 * with columns like `name`, `quantity`, `par_level`, `category`, `unit`,
 * `unit_price`, etc. Preview shows every row with its match, chef confirms,
 * apply pushes the changes to the pantry + inventory.
 */
export default function InventoryImportDialog({ open, onOpenChange, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setPreviewing(false);
    setApplying(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/inventory/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(detail || "Could not preview file.");
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleRow = (idx, nextAction) => {
    setPreview((p) => {
      if (!p) return p;
      const rows = [...p.rows];
      rows[idx] = { ...rows[idx], action: nextAction };
      const matched = rows.filter((r) => r.action === "update").length;
      const unmatched = rows.filter((r) => r.action === "create").length;
      const skipped = rows.filter((r) => r.action === "skip").length;
      return { ...p, rows, matched, unmatched, skipped };
    });
  };

  const apply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const payload = {
        rows: preview.rows.map((r) => ({
          action: r.action,
          matched_ingredient_id: r.matched_ingredient_id,
          name: r.name,
          quantity: r.quantity,
          par_level: r.par_level,
          category: r.category,
          unit: r.unit,
          unit_price: r.unit_price,
          unit_qty: r.unit_qty,
          yield_percent: r.yield_percent,
        })),
      };
      const { data } = await api.post("/inventory/import/apply", payload);
      const parts = [];
      if (data.updated_inventory) parts.push(`${data.updated_inventory} inventory rows updated`);
      if (data.created_ingredients) parts.push(`${data.created_ingredients} ingredients created`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(parts.length ? parts.join(" : ") : "Import complete.");
      if (data.errors?.length) {
        toast.error(`${data.errors.length} error(s). See details in the dialog.`);
        setPreview((p) => ({ ...p, applyErrors: data.errors }));
      } else {
        handleClose(false);
        onDone?.();
      }
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(detail || "Import failed.");
    } finally {
      setApplying(false);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "name,quantity,par_level,category,unit,unit_price,unit_qty,yield_percent",
      "Atlantic Salmon Fillet,4.5,10,food,kg,58,1,92",
      "Cultured Butter,2.0,5,food,kg,18,1,100",
      "House Red Wine,12,4,drink,bottle,22,1,100",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl" data-testid="inventory-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" style={{ color: "#f2dfb5" }} />
            Bulk update from spreadsheet
          </DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file with your stock counts. Existing ingredients get updated, new ones are created.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV or Excel file with your stock counts. Columns we understand: <span className="font-medium text-foreground">name, quantity, par_level, category, unit, unit_price, unit_qty, yield_percent</span>. Anything else is ignored.
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="import-template-btn">
                Download CSV template
              </Button>
              <div className="text-[0.65rem] text-muted-foreground">CSV : XLSX : XLS supported. PDF invoices go through Recipes › Invoice Import.</div>
            </div>
            <label
              className="flex flex-col items-center justify-center gap-2 py-10 border-2 border-dashed rounded-xl cursor-pointer transition-colors hover:bg-white/[0.02]"
              style={{ borderColor: "rgba(232,207,154,0.35)" }}
              data-testid="import-dropzone"
            >
              <UploadCloud className="w-8 h-8" style={{ color: "#f2dfb5" }} />
              <div className="text-sm font-medium">Choose a file to upload</div>
              <div className="text-xs text-muted-foreground">or drop it here</div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={onFileChange}
                data-testid="import-file-input"
              />
            </label>
            {previewing && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Reading {file?.name}
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> {preview.matched} match</div>
              <div className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" style={{ color: "#f2dfb5" }} /> {preview.unmatched} new</div>
              <div className="text-muted-foreground">{preview.skipped} skipped</div>
              <div className="ml-auto text-muted-foreground">{preview.total_rows} rows in <span className="text-foreground">{file?.name}</span></div>
            </div>
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-white/5" data-testid="import-preview-table">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03] sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">#</th>
                    <th className="px-3 py-2 font-medium">Ingredient</th>
                    <th className="px-3 py-2 font-medium text-right">Qty</th>
                    <th className="px-3 py-2 font-medium text-right">Par</th>
                    <th className="px-3 py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5" data-testid={`import-row-${i + 1}`}>
                      <td className="px-3 py-2 text-muted-foreground tabular">{r.row_number}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.name || <span className="text-destructive">missing name</span>}</div>
                        {r.matched_name && r.matched_name !== r.name && (
                          <div className="text-[0.65rem] text-muted-foreground">Matched : {r.matched_name}</div>
                        )}
                        {r.warning && <div className="text-[0.65rem] text-destructive">{r.warning}</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular">{r.quantity ?? "-"}</td>
                      <td className="px-3 py-2 text-right tabular">{r.par_level ?? "-"}</td>
                      <td className="px-3 py-2 text-right">
                        <select
                          value={r.action}
                          onChange={(e) => toggleRow(i, e.target.value)}
                          className="bg-transparent border border-white/10 rounded px-1.5 py-0.5 text-xs"
                          data-testid={`import-action-${i + 1}`}
                        >
                          <option value="update">Update</option>
                          <option value="create">Create new</option>
                          <option value="skip">Skip</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.applyErrors?.length > 0 && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs space-y-1" data-testid="import-errors">
                {preview.applyErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={reset} data-testid="import-back-btn">
                Choose a different file
              </Button>
              <Button
                onClick={apply}
                disabled={applying || (preview.matched + preview.unmatched === 0)}
                className="gap-2 gold-cta"
                data-testid="import-apply-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                Apply {preview.matched + preview.unmatched} row(s)
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
